import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { map, Observable, switchMap, of, catchError, filter, shareReplay, tap, BehaviorSubject, merge, startWith, distinctUntilChanged } from 'rxjs';
import { LocationDTO } from '../../dto/location/location.dto';
import { NavigationEnd, Router, ActivatedRoute } from '@angular/router';
import { AuthService } from '../auth/auth.service';
import { UserDTO } from '../../dto/users/user.dto';
import { AccountTypeDTO } from '../../dto/users/account-types.dto';
import { EducationalPathUserDTO } from '../../dto/educational-path/educational-path-user.dto';
import { environment } from '../../environment/environment';

interface EducationalPathsResponse {
  educational_paths: EducationalPathUserDTO[];
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly apiUrl = `${environment.apiUrl}`;
  private accountTypes$: Observable<AccountTypeDTO[]> | null = null;
  private currentUserYear$ = new BehaviorSubject<number | null>(null);
  private profileIdentity$?: Observable<{ uuid: string | null; yearParam?: number }>;
  private sharedCurrentUser$?: Observable<UserDTO>;

  constructor(
    private http: HttpClient,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService
  ) {}

  getCurrentUserFromToken(): Observable<any> {
    return this.authService.authStateReady$.pipe(
      filter(ready => ready),
      switchMap(() => this.authService.getUserInfo()),
      filter(user => user !== null),
      map(user => {
        return user;
      })
    );
  }

    getCurrentUserUuid(): string | null {
    
    // Get the UUID from the URL path
    const pathSegments = this.router.url.split('?')[0].split('/');  // Split on ? to remove query params
    
    const profileIndex = pathSegments.indexOf('profile');
    
    if (profileIndex !== -1 && pathSegments.length > profileIndex + 1) {
      const potentialUuid = pathSegments[profileIndex + 1];
      
      // Check if it's a valid UUID format
      const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (uuidPattern.test(potentialUuid)) {
        return potentialUuid;
      }
    }

    // If no UUID in path, check query params
    const queryParamUuid = this.route.snapshot.queryParamMap.get('user');
    if (queryParamUuid) {
      return queryParamUuid;
    }

    return null;
  }

  /**
   * Get current user UUID from token - use this for events, dashboard, etc.
   * This returns an Observable for async operations
   */
  getCurrentUserUuidFromToken(): Observable<string | null> {
    return this.getCurrentUserFromToken().pipe(
      map((userInfo: any) => {
        if (userInfo && userInfo.uuid) {
          return userInfo.uuid;
        }
        return null;
      }),
      catchError((error) => {
        console.error('[UserService] Error getting user UUID from JWT token:', error);
        return of(null);
      })
    );
  }

  getUserByUuid(uuid: string, year?: number): Observable<UserDTO> {
    const params = year ? new HttpParams().set('year', year.toString()) : undefined;
    return this.http.get<UserDTO>(`${this.apiUrl}/users/${uuid}`, {
      params,
      headers: { 'X-Use-Advanced-Function': 'true' },
    }).pipe(
      map(user => {
        return user;
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) {
          this.router.navigate(['/404'], { replaceUrl: true });
        }
        throw error;
      })
    );
  }

  /** Shared stream: emits when profile UUID in URL or year query param changes. */
  private getSharedProfileIdentity$(): Observable<{ uuid: string | null; yearParam?: number }> {
    if (!this.profileIdentity$) {
      this.profileIdentity$ = merge(
        this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)),
        this.route.queryParams
      ).pipe(
        startWith(null),
        map(() => {
          const year = this.route.snapshot.queryParamMap.get('year');
          return {
            uuid: this.getCurrentUserUuid(),
            yearParam: year ? parseInt(year, 10) : undefined
          };
        }),
        distinctUntilChanged(
          (prev, curr) => prev.uuid === curr.uuid && prev.yearParam === curr.yearParam
        ),
        shareReplay({ bufferSize: 1, refCount: true })
      );
    }
    return this.profileIdentity$;
  }

  /** Profile UUID from URL — updates on navigation without waiting for user HTTP. */
  getProfileUserUuid$(): Observable<string | null> {
    return this.getSharedProfileIdentity$().pipe(map(identity => identity.uuid));
  }

  getCurrentUser(): Observable<UserDTO> {
    if (!this.sharedCurrentUser$) {
      this.sharedCurrentUser$ = this.getSharedProfileIdentity$().pipe(
        switchMap(({ uuid, yearParam }) => {

          if (uuid) {
            return this.getUserByUuid(uuid, yearParam).pipe(
              tap(user => {
                if (user?.uuid && user.uuid !== uuid) {
                  console.warn('[UserService] UUID mismatch in response:', { expected: uuid, received: user.uuid });
                }
              }),
              catchError(error => {
                console.error('[UserService] Error getting user by UUID:', error);
                return of(null as unknown as UserDTO);
              })
            );
          }

          return this.getCurrentUserFromToken().pipe(
            switchMap(currentUser => {
              if (!currentUser || !currentUser.uuid) {
                return of(null as unknown as UserDTO);
              }

              return this.getUserByUuid(currentUser.uuid, yearParam);
            }),
            catchError(error => {
              console.error('[UserService] Error getting current user with year filtering:', error);
              return of(null as unknown as UserDTO);
            })
          );
        }),
        shareReplay({ bufferSize: 1, refCount: true })
      );
    }
    return this.sharedCurrentUser$;
  }

  getUserLocations(): Observable<LocationDTO[]> {
    return this.getCurrentUser().pipe(
      map(user => (user?.locations || []).map((loc: LocationDTO) => ({
        guid: loc.guid,
        locations_name: loc.locations_name,
        clean_name: loc.clean_name,
        data: loc.data || {},
        rooms: loc.rooms || [],
        users: loc.users || [],
        registered_percentage: loc.registered_percentage || 0,
        progress_percentage: loc.progress_percentage || 0,
        ok_percentage: loc.ok_percentage || 0,
        firefighter_count: loc.firefighter_count || 0
      })))
    );
  }

  getUserLocationsFromApi(): Observable<LocationDTO[]> {
    return this.http.get<LocationDTO[]>(`${this.apiUrl}/users/locations`).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('[UserService] Error fetching user locations:', error);
        if (error.status === 404) {
          throw error;
        }
        return of([]);
      })
    );
  }

  private getFirstName(user: UserDTO): string {
    return user?.first_name || '';
  }

  private getLastName(user: UserDTO): string {
    return user?.last_name || '';
  }

  getFullName(): Observable<string> {
    return this.getCurrentUser().pipe(
      map(user => {
        const firstName = this.getFirstName(user);
        const lastName = this.getLastName(user);
        return [firstName, lastName].filter(Boolean).join(' ') || 'Unknown User';
      })
    );
  }

  private filterPathsByYear(paths: EducationalPathUserDTO[], year: number | null): EducationalPathUserDTO[] {
    if (!year) return paths;
    
    const yearStart = new Date(year, 0, 1).getTime();
    const yearEnd = new Date(year, 11, 31, 23, 59, 59).getTime();
    
    return paths.filter(path => {
      const pathStart = path.recurrence_window_start ? new Date(path.recurrence_window_start).getTime() : 0;
      const pathEnd = path.recurrence_window_end ? new Date(path.recurrence_window_end).getTime() : Infinity;
      return pathStart <= yearEnd && pathEnd >= yearStart;
    });
  }

  private mapUserToEducationalPaths(user: UserDTO | null): EducationalPathUserDTO[] {
    if (!user || !user.status) {
      return [];
    }

    let pathsData = user?.status?.educational_paths || [];

    let paths: any[] = [];
    if (Array.isArray(pathsData)) {
      paths = pathsData;
    } else if (pathsData && typeof pathsData === 'object') {
      paths = Object.values(pathsData);
    } else if (pathsData) {
      paths = [pathsData];
    }

    const transformedPaths = paths.map((path, index) => {
      let pathId = path.path_id;

      if (!pathId && pathsData && typeof pathsData === 'object' && !Array.isArray(pathsData)) {
        const keys = Object.keys(pathsData);
        if (keys[index]) {
          pathId = parseInt(keys[index], 10);
        }
      }

      if (!pathId && path.base_path_id) {
        pathId = path.base_path_id;
      }

      const rawPath = path as Record<string, unknown>;
      const rawManual =
        rawPath['maintenance_minutes_manual'] ??
        rawPath['maintenanceMinutesManual'];
      const parsedManual =
        rawManual === undefined || rawManual === null || rawManual === ''
          ? 0
          : Math.max(0, Number(rawManual));
      const rawAcquire =
        rawPath['maintenance_manual_acquire_all'] ??
        rawPath['maintenanceManualAcquireAll'];

      const transformed = {
        ...path,
        user: user,
        version_id: path.version_id || 0,
        commitment_type: path.commitment_type || 'requirement',
        requirement_started_at: path.requirement_started_at || null,
        maintenance_started_at: path.maintenance_started_at || null,
        assigned_at: path.assigned_at || new Date().toISOString(),
        progress_percentage: path.progress_percentage || 0,
        total_items: path.total_items || 0,
        completed_items: path.completed_items || 0,
        maintenance_minutes_required: path.maintenance_minutes_required || 0,
        maintenance_minutes_completed: path.maintenance_minutes_completed || 0,
        maintenance_minutes_intended: Number(path.maintenance_minutes_intended) || 0,
        registered_percentage: path.registered_percentage ?? 0,
        maintenance_minutes_manual: Number.isFinite(parsedManual) ? parsedManual : 0,
        maintenance_manual_acquire_all:
          rawAcquire === true ||
          rawAcquire === 'true' ||
          rawAcquire === 1,
        manual_completed_modules: Array.isArray(rawPath['manual_completed_modules'])
          ? rawPath['manual_completed_modules']
          : [],
        recurrence_window_start: path.recurrence_window_start || null,
        recurrence_window_end: path.recurrence_window_end || null,
        recurrence_value: path.recurrence_value || null,
        recurrence_unit: path.recurrence_unit || null,
        recurrence_type: path.recurrence_type || 'calendar',
        requirements: path.requirements || [],
        maintenance: path.maintenance || [],
        certification_url: path.certification_url,
        path_id: pathId,
        base_path_id: path.base_path_id,
        name: path.name || 'Unavngivet uddannelsessti',
        full_name: path.full_name || path.name || 'Unavngivet uddannelsessti',
        guid: path.guid
      };
      return transformed;
    });

    return transformedPaths;
  }

  /** Map user status payload to educational path DTOs (e.g. participate form hints). */
  educationalPathsFromUser(user: UserDTO | null): EducationalPathUserDTO[] {
    return this.mapUserToEducationalPaths(user);
  }

  getEducationalPaths(): Observable<EducationalPathUserDTO[]> {
    return this.getCurrentUser().pipe(
      tap(user => {
        if (!user) {
        } else {
        }
      }),
      map(user => this.mapUserToEducationalPaths(user)),
      catchError(error => {
        console.error('[UserService] Error in getEducationalPaths:', error);
        return of([]);
      })
    );
  }

  /** Fetch achieved paths for an explicit user UUID (bypasses URL-based identity). */
  getAchievedPathsForUuid(uuid: string, year?: number): Observable<EducationalPathUserDTO[]> {
    if (!uuid) {
      return of([]);
    }
    return this.getUserByUuid(uuid, year).pipe(
      map(user => {
        if (user?.uuid && user.uuid !== uuid) {
          console.warn('[UserService] getAchievedPathsForUuid mismatch:', { expected: uuid, received: user.uuid });
          return [];
        }
        return this.mapUserToEducationalPaths(user);
      }),
      catchError(error => {
        console.error('[UserService] Error getting achieved paths for UUID:', uuid, error);
        return of([]);
      })
    );
  }

  getAchievedPaths(): Observable<EducationalPathUserDTO[]> {
    return this.getEducationalPaths().pipe(
      map(paths => {
        // If paths is undefined, null or not an array, return empty array
        if (!paths || !Array.isArray(paths)) {
          return [];
        }

        const filteredPaths = paths.filter(path => {
          return true; // Return all paths since filtering now happens on the backend
        });
        return filteredPaths;
      }),
      catchError(error => {
        console.error('[UserService] Error getting achieved paths:', error);
        return of([]); // Return empty array on error
      })
    );
  }

  getAccountTypes(): Observable<AccountTypeDTO[]> {
    if (!this.accountTypes$) {
      this.accountTypes$ = this.http.get<AccountTypeDTO[]>(`${this.apiUrl}/users/accounttypes`).pipe(
        tap(response => {
        }),
        catchError((error: HttpErrorResponse) => {
          console.error('[UserService] Error fetching account types:', {
            status: error.status,
            statusText: error.statusText,
            message: error.message,
            error: error.error,
            url: error.url
          });
          return of([]);
        }),
        shareReplay(1)
      );
    }
    return this.accountTypes$;
  }
}
